import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getAdminUsers, createUser, updateUser, deleteUser, resetUserPassword, bulkCreateUsers } from '../api';

const POSITIONS = [
  'Occupational Therapist',
  'Speech-Language Pathologist',
  'School Psychologist',
  'Special Education Teacher',
  'Administrator',
  'Other',
];

export default function AdminUsers() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [form, setForm] = useState({ email: '', password: '', first_name: '', last_name: '', role: 'employee', position: '' });
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    const res = await getAdminUsers();
    setUsers(res.data);
    setLoading(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await createUser(form);
      setMsg('Employee created successfully!');
      setShowAdd(false);
      setForm({ email: '', password: '', first_name: '', last_name: '', role: 'employee', position: '' });
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not create employee.');
    }
  };

  const handleBulkUpload = async (e) => {
    e.preventDefault();
    if (!bulkFile) return;
    setBulkLoading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', bulkFile);
      const res = await bulkCreateUsers(formData);
      setBulkResult(res.data);
      setMsg(res.data.message);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not process CSV file.');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await updateUser(editUser.id, editUser);
      setMsg('Employee updated successfully!');
      setEditUser(null);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update employee.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this employee?')) return;
    await deleteUser(id);
    setMsg('Employee deleted.');
    fetchUsers();
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    try {
      await resetUserPassword(resetUser.id, { new_password: newPassword });
      setMsg('Password reset successfully!');
      setResetUser(null);
      setNewPassword('');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not reset password.');
    }
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  return (
    <div style={styles.container}>
      <nav style={styles.nav}>
        <div style={styles.navBrand}>
          <span style={styles.navTitle}>Boothby Therapy Services</span>
          <span style={styles.navSubtitle}>Admin Panel</span>
        </div>
        <div style={styles.navRight}>
          <button onClick={() => navigate('/admin')} style={styles.navBtn}>🏠 Dashboard</button>
          <button onClick={() => navigate('/admin/resources')} style={styles.navBtn}>📚 Resources</button>
          <button onClick={() => navigate('/admin/profile')} style={styles.navBtn}>⚙️ Settings</button>
          <button onClick={handleLogout} style={styles.navBtnOutline}>Sign Out</button>
        </div>
      </nav>

      <div style={styles.content}>
        <div style={styles.header}>
          <h1 style={styles.pageTitle}>Manage Employees</h1>
          <div style={styles.headerBtns}>
            <button onClick={() => setShowBulk(true)} style={styles.secondaryBtn}>📋 Bulk Import</button>
            <button onClick={() => setShowAdd(true)} style={styles.addBtn}>+ Add Employee</button>
          </div>
        </div>

        {msg && <div style={styles.success}>{msg}</div>}
        {error && <div style={styles.error}>{error}</div>}

        {/* Bulk Import Modal */}
        {showBulk && (
          <div style={styles.modal}>
            <div style={styles.modalCard}>
              <h2 style={styles.modalTitle}>Bulk Import Employees</h2>
              <div style={styles.infoBox}>
                <p style={styles.infoText}>Upload a CSV file with these columns:</p>
                <code style={styles.codeText}>first_name, last_name, email, password, position, role</code>
                <p style={styles.infoText}>Example:</p>
                <code style={styles.codeText}>John, Doe, john@example.com, Pass123, Occupational Therapist, employee</code>
              </div>
              <form onSubmit={handleBulkUpload} style={styles.form}>
                <div style={styles.field}>
                  <label style={styles.label}>CSV File</label>
                  <input
                    type="file"
                    accept=".csv"
                    style={styles.input}
                    onChange={(e) => setBulkFile(e.target.files[0])}
                    required
                  />
                </div>
                {bulkResult && (
                  <div style={styles.bulkResult}>
                    <p style={{color: '#16a34a', fontWeight: '600'}}>✅ Created: {bulkResult.results.success.length}</p>
                    <p style={{color: '#dc2626', fontWeight: '600'}}>❌ Failed: {bulkResult.results.failed.length}</p>
                    {bulkResult.results.failed.map((f, i) => (
                      <p key={i} style={{color: '#dc2626', fontSize: '12px'}}>Row {f.row}: {f.reason}</p>
                    ))}
                  </div>
                )}
                <div style={styles.modalBtns}>
                  <button type="submit" style={styles.addBtn} disabled={bulkLoading}>
                    {bulkLoading ? 'Importing...' : 'Import CSV'}
                  </button>
                  <button type="button" onClick={() => { setShowBulk(false); setBulkResult(null); }} style={styles.cancelBtn}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Add Employee Modal */}
        {showAdd && (
          <div style={styles.modal}>
            <div style={styles.modalCard}>
              <h2 style={styles.modalTitle}>Add New Employee</h2>
              <form onSubmit={handleCreate} style={styles.form}>
                <div style={styles.row}>
                  <div style={styles.field}>
                    <label style={styles.label}>First Name</label>
                    <input style={styles.input} value={form.first_name} onChange={(e) => setForm({...form, first_name: e.target.value})} required />
                  </div>
                  <div style={styles.field}>
                    <label style={styles.label}>Last Name</label>
                    <input style={styles.input} value={form.last_name} onChange={(e) => setForm({...form, last_name: e.target.value})} required />
                  </div>
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Email</label>
                  <input type="email" style={styles.input} value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} required />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Password</label>
                  <input type="password" style={styles.input} value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} required />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Position</label>
                  <select style={styles.input} value={form.position} onChange={(e) => setForm({...form, position: e.target.value})}>
                    <option value="">Select position...</option>
                    {POSITIONS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Role</label>
                  <select style={styles.input} value={form.role} onChange={(e) => setForm({...form, role: e.target.value})}>
                    <option value="employee">Employee</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div style={styles.modalBtns}>
                  <button type="submit" style={styles.addBtn}>Create Employee</button>
                  <button type="button" onClick={() => setShowAdd(false)} style={styles.cancelBtn}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Employee Modal */}
        {editUser && (
          <div style={styles.modal}>
            <div style={styles.modalCard}>
              <h2 style={styles.modalTitle}>Edit Employee</h2>
              <form onSubmit={handleUpdate} style={styles.form}>
                <div style={styles.row}>
                  <div style={styles.field}>
                    <label style={styles.label}>First Name</label>
                    <input style={styles.input} value={editUser.first_name} onChange={(e) => setEditUser({...editUser, first_name: e.target.value})} required />
                  </div>
                  <div style={styles.field}>
                    <label style={styles.label}>Last Name</label>
                    <input style={styles.input} value={editUser.last_name} onChange={(e) => setEditUser({...editUser, last_name: e.target.value})} required />
                  </div>
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Position</label>
                  <select style={styles.input} value={editUser.position} onChange={(e) => setEditUser({...editUser, position: e.target.value})}>
                    <option value="">Select position...</option>
                    {POSITIONS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Role</label>
                  <select style={styles.input} value={editUser.role} onChange={(e) => setEditUser({...editUser, role: e.target.value})}>
                    <option value="employee">Employee</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Status</label>
                  <select style={styles.input} value={editUser.is_active} onChange={(e) => setEditUser({...editUser, is_active: e.target.value === 'true'})}>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
                <div style={styles.modalBtns}>
                  <button type="submit" style={styles.addBtn}>Save Changes</button>
                  <button type="button" onClick={() => setEditUser(null)} style={styles.cancelBtn}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Reset Password Modal */}
        {resetUser && (
          <div style={styles.modal}>
            <div style={styles.modalCard}>
              <h2 style={styles.modalTitle}>Reset Password for {resetUser.first_name}</h2>
              <form onSubmit={handleResetPassword} style={styles.form}>
                <div style={styles.field}>
                  <label style={styles.label}>New Password</label>
                  <input type="password" style={styles.input} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
                </div>
                <div style={styles.modalBtns}>
                  <button type="submit" style={styles.addBtn}>Reset Password</button>
                  <button type="button" onClick={() => setResetUser(null)} style={styles.cancelBtn}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Users Table */}
        {loading ? (
          <div style={styles.loading}>Loading employees...</div>
        ) : (
          <div style={styles.tableCard}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.thead}>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Position</th>
                  <th style={styles.th}>Role</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} style={styles.tr}>
                    <td style={styles.td}>{u.first_name} {u.last_name}</td>
                    <td style={styles.td}>{u.email}</td>
                    <td style={styles.td}>{u.position || '—'}</td>
                    <td style={styles.td}>
                      <span style={u.role === 'admin' ? styles.badgeAdmin : styles.badgeEmployee}>
                        {u.role}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={u.is_active ? styles.badgeActive : styles.badgeInactive}>
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actions}>
                        <button onClick={() => setEditUser(u)} style={styles.editBtn}>Edit</button>
                        <button onClick={() => setResetUser(u)} style={styles.resetBtn}>Reset PW</button>
                        <button onClick={() => handleDelete(u.id)} style={styles.deleteBtn}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', background: '#f8fafc', overflowX: 'hidden' },
  nav: { background: '#243C94', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' },
  navBrand: { display: 'flex', flexDirection: 'column' },
  navTitle: { color: '#ffffff', fontSize: '16px', fontWeight: '700' },
  navSubtitle: { color: '#FFA204', fontSize: '11px', fontWeight: '600', letterSpacing: '1px' },
  navRight: { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' },
  navBtn: { background: 'rgba(255,255,255,0.15)', color: '#ffffff', border: 'none', borderRadius: '8px', padding: '6px 10px', cursor: 'pointer', fontSize: '12px' },
  navBtnOutline: { background: 'transparent', color: '#ffffff', border: '2px solid rgba(255,255,255,0.5)', borderRadius: '8px', padding: '6px 10px', cursor: 'pointer', fontSize: '12px' },
  content: { maxWidth: '1200px', margin: '0 auto', padding: '24px 16px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' },
  headerBtns: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  pageTitle: { color: '#243C94', fontSize: '24px', fontWeight: '700', margin: 0 },
  addBtn: { background: '#FFA204', color: '#ffffff', border: 'none', borderRadius: '8px', padding: '10px 20px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  secondaryBtn: { background: '#243C94', color: '#ffffff', border: 'none', borderRadius: '8px', padding: '10px 20px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  cancelBtn: { background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '8px', padding: '10px 20px', cursor: 'pointer', fontSize: '14px' },
  success: { background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#16a34a', padding: '12px', borderRadius: '8px', fontSize: '14px', marginBottom: '16px' },
  error: { background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '12px', borderRadius: '8px', fontSize: '14px', marginBottom: '16px' },
  infoBox: { background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px', marginBottom: '8px' },
  infoText: { color: '#374151', fontSize: '13px', margin: '0 0 6px 0' },
  codeText: { display: 'block', background: '#e5e7eb', padding: '6px 10px', borderRadius: '4px', fontSize: '12px', color: '#243C94', marginBottom: '6px' },
  bulkResult: { background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px' },
  modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' },
  modalCard: { background: '#ffffff', borderRadius: '12px', padding: '24px', width: '100%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto' },
  modalTitle: { color: '#243C94', fontSize: '20px', fontWeight: '700', marginBottom: '24px' },
  form: { display: 'flex', flexDirection: 'column', gap: '16px' },
  row: { display: 'flex', gap: '16px', flexWrap: 'wrap' },
  field: { display: 'flex', flexDirection: 'column', gap: '6px', flex: 1, minWidth: '120px' },
  label: { color: '#374151', fontSize: '14px', fontWeight: '600' },
  input: { padding: '10px 14px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '14px' },
  modalBtns: { display: 'flex', gap: '12px', marginTop: '8px', flexWrap: 'wrap' },
  tableCard: { background: '#ffffff', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: '600px' },
  thead: { background: '#f8fafc' },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#6b7280', borderBottom: '1px solid #e5e7eb' },
  tr: { borderBottom: '1px solid #f0f0f0' },
  td: { padding: '12px 16px', fontSize: '14px', color: '#374151' },
  actions: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  editBtn: { background: '#eff6ff', color: '#243C94', border: 'none', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' },
  resetBtn: { background: '#fff7ed', color: '#ea580c', border: 'none', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' },
  deleteBtn: { background: '#fef2f2', color: '#dc2626', border: 'none', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' },
  badgeAdmin: { background: '#243C94', color: '#ffffff', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' },
  badgeEmployee: { background: '#f3f4f6', color: '#374151', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' },
  badgeActive: { background: '#f0fdf4', color: '#16a34a', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' },
  badgeInactive: { background: '#fef2f2', color: '#dc2626', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' },
  loading: { textAlign: 'center', padding: '60px', color: '#6b7280' },
};